import { useCallback, useEffect, useRef } from 'react';

import { getRandomNum } from 'buyside/util/numbers';
import { Maybe } from 'interfaces/graphql-typings';

// https://www.joshwcomeau.com/snippets/react-hooks/use-random-interval/
const useInterval = (
  callback: () => void,
  minDelay: Maybe<number>,
  maxDelay: Maybe<number>
): (() => void) => {
  const timeoutId = useRef<Maybe<number>>(null);

  const doSomething = useCallback(() => callback(), [callback]);

  useEffect(() => {
    if (typeof minDelay === 'number' && typeof maxDelay === 'number') {
      // On every tick, schedule the next iteration after a period of time in the future,
      // based on minDelay and maxDelay.
      const handleTick = (): void => {
        const nextTickAt = minDelay === maxDelay ? minDelay : getRandomNum(minDelay, maxDelay);
        timeoutId.current = window.setTimeout(() => {
          doSomething();
          handleTick();
        }, nextTickAt);
      };
      handleTick();
    }

    // Called whenever the delays change to clear the timeout without setting a new timeout.
    return (): void => window.clearTimeout(timeoutId.current || undefined);
  }, [doSomething, minDelay, maxDelay]);

  // Interrupt the loop without triggering a re-render. Shouldn't be used in most cases.
  return useCallback(() => window.clearTimeout(timeoutId.current || undefined), []);
};

export default useInterval;
